import React, { useState } from 'react'

interface LocationPermissionModalProps {
  isOpen: boolean
  onRequestLocation: () => Promise<void>
  onClose: () => void
  onSkip: () => void
}

export function LocationPermissionModal({ 
  isOpen, 
  onRequestLocation, 
  onClose, 
  onSkip 
}: LocationPermissionModalProps) {
  const [isRequesting, setIsRequesting] = useState(false)

  if (!isOpen) return null

  const handleRequestLocation = async () => {
    setIsRequesting(true)
    try {
      await onRequestLocation()
      onClose()
    } catch (error) {
      console.error('Location request failed:', error)
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[20000] p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="text-center">
          {/* Location Icon */}
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg 
              className="h-6 w-6 text-blue-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
              />
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
              />
            </svg>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Enable Location Services
          </h3>
          
          <p className="text-sm text-gray-600 mb-6">
            We need your location to find nearby parking spots and provide accurate directions. 
            Your location data is only used to enhance your parking experience and is never stored or shared.
          </p>

          <div className="space-y-3">
            <button
              onClick={handleRequestLocation}
              disabled={isRequesting}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isRequesting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Requesting Location...
                </>
              ) : (
                'Allow Location Access'
              )}
            </button>

            <button
              onClick={onSkip}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200"
            >
              Skip for Now
            </button>
          </div>

          <div className="mt-4 text-xs text-gray-500">
            <p className="mb-2">
              <strong>Having trouble?</strong> If location access was denied:
            </p>
            <ul className="text-left space-y-1">
              <li>• On iPhone: Settings → Safari → Location Services</li>
              <li>• On Android: Settings → Site Settings → Location</li>
              <li>• On Desktop: Click the location icon in your address bar</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
